import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, Warning as WarningIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { TranslationKeys } from '../../../i18n';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.warning.main,
  color: theme.palette.warning.contrastText,
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '& .MuiIconButton-root': {
    color: theme.palette.warning.contrastText,
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    minWidth: '400px',
    maxWidth: '500px',
  },
}));

interface DiscardChangesModalProps {
  open: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onSaveAsDraft: () => void;
  onContinueEditing: () => void;
  loading?: boolean;
}

const DiscardChangesModal: React.FC<DiscardChangesModalProps> = ({
  open,
  onClose,
  onDiscard,
  onSaveAsDraft,
  onContinueEditing,
  loading = false,
}) => {
  useTranslation<TranslationKeys>();

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon />
          <Typography variant="h6" component="div" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Unsaved Changes
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={onClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent sx={{ padding: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes that will be lost if you leave this page.
        </Alert>
        
        <Typography variant="body1" sx={{ mb: 2 }}>
          What would you like to do with your changes?
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          • <strong>Discard Changes:</strong> All your changes will be lost and you'll return to the dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • <strong>Save as Draft:</strong> Your current progress will be saved and you can continue later
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • <strong>Continue Editing:</strong> Stay on this page and keep working
        </Typography>
      </DialogContent>

      <DialogActions sx={{ padding: 3, paddingTop: 1, gap: 1 }}>
        <Button
          onClick={onContinueEditing}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 140, textTransform: 'none' }}
        >
          Continue Editing
        </Button>
        <Button
          onClick={onSaveAsDraft}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ minWidth: 120, textTransform: 'none' }}
        >
          Save as Draft
        </Button>
        <Button
          onClick={onDiscard}
          variant="contained"
          color="error"
          disabled={loading}
          sx={{ minWidth: 100, textTransform: 'none' }}
        >
          Discard
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default DiscardChangesModal;